const DONE_STATUS = 4;
const PROCESSING_STATUS = 1;
const PENDING_STATUS = 0;

const isTaskDone = (task) => task?.status === DONE_STATUS;

// type=1(新增对象数据) 是否满足完成条件由后端返回的 businessValue 决定，不再直接复用任务状态。
const hasTaskBusinessValue = (businessValue) => {
  if (Array.isArray(businessValue)) {
    return businessValue.length > 0;
  }

  return businessValue !== null && businessValue !== undefined && businessValue !== '';
};

// type=2 任务的可完成性依赖表单 validator，不能只靠状态判断。
const shouldValidateTypeTwoTask = (task) => task?.type === 2 && [PENDING_STATUS, PROCESSING_STATUS].includes(task?.status);

// type=3(提示任务) 在完成节点时走固定口径：必做视为满足，非必做视为不满足。
const isTypeThreeTaskEligible = (task) => Boolean(task?.required);

// 非表单任务沿用当前任务状态口径判断是否可随节点一起完成。
export const isTaskEligibleByStatus = (task) => {
  if (!task || isTaskDone(task)) return false;

  if (task.type === 1) {
    return hasTaskBusinessValue(task.businessValue);
  }

  if (task.type === 2) {
    return [PENDING_STATUS, PROCESSING_STATUS].includes(task.status);
  }

  if (task.type === 3) {
    return isTypeThreeTaskEligible(task);
  }

  return false;
};

// 兼容 boolean / object 两类校验返回，统一成 { pass } 结构给上层消费。
const normalizeValidateResult = (result) => {
  if (typeof result === 'boolean') {
    return { pass: result };
  }

  if (result && typeof result === 'object') {
    return result;
  }

  return { pass: false };
};

// 提交给后端前做一次去重，避免多次遍历或异常数据导致相同任务 ID 重复提交。
const uniqueTaskIds = (taskIds) => [...new Set(taskIds.filter((id) => id !== null && id !== undefined && id !== ''))];

// 节点完成前统一汇总任务结果：必做未满足则阻断，灰度开启时才真正收集 nodeTaskIds。
export const buildNodeTaskIdsPayload = async ({ grayEnabled = false, tasks = [], validateTypeTwoTask = async () => ({ pass: false }) } = {}) => {
  const validTaskIds = [];
  const blockedTasks = [];

  for (const task of tasks) {
    if (!task || isTaskDone(task)) continue;

    let isEligible = false;

    if (shouldValidateTypeTwoTask(task)) {
      let validateResult = { pass: false };

      try {
        validateResult = normalizeValidateResult(await validateTypeTwoTask(task));
      } catch (error) {
        // 校验异常直接 fail-closed，避免节点完成流程在异常场景下继续向后执行。
        return {
          pass: false,
          nodeTaskIds: [],
          blockedTasks: [],
          message: error?.message || '任务校验失败，请稍后重试'
        };
      }

      if (validateResult?.notReady) {
        // 子表单未就绪时不再静默吞掉任务，统一让上层提示后重试，保证 payload 稳定。
        return {
          pass: false,
          nodeTaskIds: [],
          blockedTasks: [],
          message: validateResult?.message || '任务表单未初始化，请稍后重试'
        };
      }

      isEligible = Boolean(validateResult?.pass);
    } else {
      isEligible = isTaskEligibleByStatus(task);
    }

    if (isEligible) {
      if (grayEnabled) {
        validTaskIds.push(task.id);
      }
      continue;
    }

    if (task.required) {
      blockedTasks.push(task);
    }
  }

  return {
    pass: blockedTasks.length === 0,
    nodeTaskIds: blockedTasks.length ? [] : uniqueTaskIds(validTaskIds),
    blockedTasks
  };
};
